import ab, { DynamicABTestBucket, DynamicABTestID } from '../ruleset/ab';
import {
  getDynamicTestBucket,
  isDynamicTestInBucket,
} from 'src/replicant/getters/ab';
import features from 'src/replicant/ruleset/features';
import { guessIsNativeAppInstalled } from 'src/replicant/native/native.getters';
import { ScheduledActionAPI, SyncActionAPI } from '../utils/types';
import { MutableState } from '../State';

/**
 * Arguments for the `assignManualAbTests` function.
 */
export type AssignManualAbTestArgs = {
  isNewPlayer?: boolean;
  isPlatformNative: boolean;
  isNativeIOS: boolean;
  isNativeAndroid: boolean;
  isWebAndroid?: boolean;
  isInLineMiniApp: boolean;
  nativeIOSVersion?: string;
  demoModeSubdomain: boolean;
  isPlatformLiff: boolean;
  isNativeSupportLineLoginChannel?: boolean;
};

/**
 * Helper to create a function that assigns an AB test only once.
 * @param state
 * @param api
 * @returns
 */
export function getAssignOnceFn(
  state: MutableState,
  api: Pick<SyncActionAPI | ScheduledActionAPI, 'abTests'>,
) {
  // Manually assign an AB test if no bucket is assigned yet.
  return <T extends DynamicABTestID>(
    testId: T,
    bucket?: DynamicABTestBucket<T>,
  ) => {
    if (getDynamicTestBucket(state, testId)) return;
    api.abTests.assign(testId, bucket);
  };
}

/**
 * Assigns manual AB tests based on the given arguments.
 * @param state
 * @param args
 * @param api
 */
export function assignManualAbTests(
  state: MutableState,
  args: AssignManualAbTestArgs,
  api: SyncActionAPI,
): void {
  // Manually assign an AB test if no bucket is assigned yet.
  const assignOnce = getAssignOnceFn(state, api);

  // Assign level based tests with this.
  const assignLevelTest = (testId: DynamicABTestID, maxLevel: number) => {
    // Assign only for players that are 100 levels away from the first AB test level
    const minLevel = maxLevel - 100;
    if (state.match2.level >= minLevel && state.match2.level < maxLevel) {
      assignOnce(testId);
    }
  };

  // Check if the player is a new player, if the argument is not provided
  const isNewPlayer = args.isNewPlayer ?? state.loginCount === 1;

  // Only assign `pure_open_web_v2` if new user and not on native app
  if (isNewPlayer && !args.isPlatformNative) {
    // #6218: assign if new player not on native (same as pure_open_web before)
    assignOnce(ab.tests.FEATURE_OPEN_WEB_V2);
  }

  // #6218: assign if player was on pure_open_web and his first session was not on native (the previous bug...)
  if (
    !args.isPlatformNative &&
    // prettier-ignore
    getDynamicTestBucket(state, ab.tests.FEATURE_PURE_OPEN_WEB) &&
    !guessIsNativeAppInstalled(state, 'IOS', api.date.now(), 30) &&
    !guessIsNativeAppInstalled(state, 'ANDROID', api.date.now(), 30)
  ) {
    assignOnce(ab.tests.FEATURE_OPEN_WEB_V2);
  }

  // #6218: assign if 242 is enabled and player does not have any native session in the last 30 days
  if (
    !args.isPlatformNative &&
    // prettier-ignore
    isDynamicTestInBucket(state, ab.tests.TEST_OPEN_WEB_PAYMENT, 'enabled_iab') &&
    !guessIsNativeAppInstalled(state, 'IOS', api.date.now(), 30) &&
    !guessIsNativeAppInstalled(state, 'ANDROID', api.date.now(), 30)
  ) {
    assignOnce(ab.tests.FEATURE_OPEN_WEB_V2);
  }

  // assign expanded pure open web test to existing users who are not in any of the existing open web tests
  if (
    !getDynamicTestBucket(state, ab.tests.FEATURE_OPEN_WEB_V2) &&
    !getDynamicTestBucket(state, ab.tests.TEST_OPEN_WEB_PAYMENT) &&
    !getDynamicTestBucket(state, ab.tests.TEST_PURE_OPEN_WEB_V2) &&
    !getDynamicTestBucket(state, ab.tests.FEATURE_PURE_OPEN_WEB) &&
    !guessIsNativeAppInstalled(state, 'IOS', api.date.now(), 30) &&
    !guessIsNativeAppInstalled(state, 'ANDROID', api.date.now(), 30) &&
    !args.isNativeAndroid &&
    !args.isNativeIOS
  ) {
    assignOnce(ab.tests.FEATURE_OPEN_WEB_V2);
  }

  // #6218: assign if the player was on 0309_pure_open_web_v2 = enabled
  if (
    !args.isPlatformNative &&
    // prettier-ignore
    isDynamicTestInBucket(state, ab.tests.TEST_PURE_OPEN_WEB_V2, 'enabled_web')
  ) {
    assignOnce(ab.tests.FEATURE_OPEN_WEB_V2);
  }

  // Only demo subdomain and guest mode
  if (features(state).isGuestMode && args.demoModeSubdomain) {
    assignOnce(ab.tests.TEST_DEMO_MODE);
  }

  // Assign new-users-only tests
  if (isNewPlayer) {
    assignOnce(ab.tests.TEST_NEW_FIRST_2_OBSTACLE_VISUALS);
  }

  // Assign Native Crossplay feature flags
  // We want to keep existing native users experience unchanged
  // If crossplay feature flag is `enabled` we should prioritize native app over mini app
  // If user do not meet the criteria anymore we set crossplay feature flag to `disabled`
  // Criteria is:
  // - Has native app installed in last 90 days OR
  // - Is on native app at time of assignment
  // - Not in guest mode
  if (!features(state).isGuestMode) {
    if (
      args.isNativeIOS ||
      guessIsNativeAppInstalled(state, 'IOS', api.date.now(), 90)
    ) {
      assignOnce(ab.tests.FEATURE_NATIVE_CROSSPLAY_IOS, 'enabled');
    }
    if (
      args.isNativeAndroid ||
      guessIsNativeAppInstalled(state, 'ANDROID', api.date.now(), 90)
    ) {
      assignOnce(ab.tests.FEATURE_NATIVE_CROSSPLAY_ANDROID, 'enabled');
    }

    // Disable crossplay feature flags for users who do not meet the criteria anymore
    if (
      !args.isNativeIOS &&
      !guessIsNativeAppInstalled(state, 'IOS', api.date.now(), 90) &&
      isDynamicTestInBucket(
        state,
        ab.tests.FEATURE_NATIVE_CROSSPLAY_IOS,
        'enabled',
      )
    ) {
      api.abTests.assign(ab.tests.FEATURE_NATIVE_CROSSPLAY_IOS, 'disabled');
    }
    if (
      !args.isNativeAndroid &&
      !guessIsNativeAppInstalled(state, 'ANDROID', api.date.now(), 90) &&
      isDynamicTestInBucket(
        state,
        ab.tests.FEATURE_NATIVE_CROSSPLAY_ANDROID,
        'enabled',
      )
    ) {
      api.abTests.assign(ab.tests.FEATURE_NATIVE_CROSSPLAY_ANDROID, 'disabled');
    }
  }
}

/**
 * Arguments for the `onBeforeEntryFinal` action.
 */
export type AssignManualAbTestBeforeEntryFinalArgs = {
  isAdult: boolean;
  /**
   * Whether the user is running through LINE Mini App.
   * @deprecated Unused since A/B 0494 resolved to Control.
   */
  isInLineMiniApp: boolean;
  /**
   * Whether the user is subscribed to the LINE Official Account.
   * @deprecated Unused since A/B 0494 resolved to Control.
   */
  isSubscribedToOA?: boolean;
};
